//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Order, OrderDetail, OrderJob } from '@/redux/slices/slice_types';
import { findandFormatUTCDateInString } from '@tmt-insights/oms_fe_core/date';
import {
  ClickCopy,
  DateTimeDisplay,
  EditableTextCell,
  Notes,
  OrdersOverflowMenu,
  OrdersProgressBar,
  OrdersRowExpand,
  TableStatus,
  UserDisplay
} from '@tmt-insights/oms_fe_core/columns';

export default {
  expandable: {
    label: '',
    isExpandable: true,
    disableExpand: (row: OrderDetail) => row.status === 'draft' || row.order_type === 'bulk',
    render: (props: { item: Order; isCollapsed: boolean }) => {
      const extraColumns = [
        {
          label: 'Version',
          width: 200,
          render: (job: OrderJob) => (
            <ClickCopy noBackground disableTextClick>
              {job.version?.name || '-'}
            </ClickCopy>
          )
        },
        {
          label: 'Profile',
          width: 200,
          render: (job: OrderJob) => (
            <ClickCopy copyText={findandFormatUTCDateInString(job?.profile?.name) || ''} noBackground disableTextClick>
              {findandFormatUTCDateInString(job?.profile?.name)}
            </ClickCopy>
          )
        },
        {
          label: 'Due Date',
          width: 100,
          render: (job: OrderJob) => (
            <ClickCopy noBackground disableTextClick>
              <DateTimeDisplay>{job.due_date as string}</DateTimeDisplay>
            </ClickCopy>
          )
        }
      ];
      return <OrdersRowExpand extraColumns={extraColumns} {...props} />;
    }
  },
  hid: {
    label: 'Order ID',
    clickCopy: true,
    sortField: 'id',
    minWidth: 110,
    render: (cell: OrderJob['id']) => <strong style={{ whiteSpace: 'nowrap' }}>{cell || '-'}</strong>
  },
  description: {
    label: 'Description',
    clickCopy: true,
    minWidth: 210,
    render: (cell: OrderJob['description'], row: OrderDetail) => (
      <EditableTextCell value={cell} row={row} update="order" field="description" emptyText="-" />
    ),
    required: true
  },
  total_jobs: {
    label: 'Job(s)',
    disableSort: true,
    render: (cell: OrderJob['total_jobs']) => (cell > 0 ? cell : '-')
  },
  po_number: {
    label: 'PO #',
    clickCopy: true,
    minWidth: 210,
    render: (cell: Order['po_number'], row: OrderDetail) => (
      <EditableTextCell value={cell} row={row} update="order" label="PO #" field="po_number" emptyText="-" />
    )
  },
  cost_centre: {
    label: 'Cost Centre',
    clickCopy: true,
    minWidth: 210,
    render: (cell: Order['cost_centre'], row: OrderDetail) =>
      row?.status?.toLowerCase() === 'complete' ? (
        cell || '-'
      ) : (
        <EditableTextCell value={cell} row={row} update="order" label="Cost Centre" field="cost_centre" emptyText="-" />
      )
  },
  opportunity_id: {
    label: 'Opportunity ID',
    clickCopy: true,
    minWidth: 210,
    render: (cell: Order['opportunity_id'], row: OrderDetail) =>
      row?.status?.toLowerCase() === 'complete' ? (
        cell || '-'
      ) : (
        <EditableTextCell value={cell} row={row} update="order" label="Opportunity ID" field="opportunity_id" emptyText="-" />
      )
  },
  managed_by_user: {
    label: 'Managed by',
    minWidth: 100,
    disableSort: true,
    // when all clients support changing managed_by, we can use this
    // render: (cell, row: Order) => <AssigneeSelect value={cell} id={row.id} row={row} update="order" assigns="user" emptyText="-" />
    render: (cell: Order['managed_by_user'], row: Order) => (cell ? <UserDisplay user={cell} emptyText="-" /> : row.managed_by) || '-'
  },
  billable_customer: {
    label: 'Billable Customer'
  },
  assigned_to_vendor: {
    label: 'Assigned To Vendor',
    sortField: 'assigned_to_vendor',
    minWidth: 180,
    render: (cell: string | null) => cell || '-'
  },
  source_id: {
    label: 'Source ID',
    sortField: 'source_id_sort',
    clickCopy: true,
    minWidth: 180,
    render: (cell: string | null) => cell || '-'
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    width: 100,
    render: (cell: OrderDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  completed_at: {
    label: 'Completed',
    clickCopy: true,
    width: 100,
    render: (cell: OrderDetail['completed_at']) => <DateTimeDisplay missingText={'-'}>{cell}</DateTimeDisplay>
  },

  status: {
    label: 'Status',
    render: (cell: OrderDetail['status']) => <TableStatus status={cell} />
  },
  jobs: {
    label: 'Progress',
    width: 100,
    disableSort: true,
    render: (jobs: Order['jobs'], row: Order) => <OrdersProgressBar jobs={jobs} order={row} />
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: Order) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },
  overflow: {
    label: '',
    isOverflow: true,
    width: 50,
    render: (_cell: any, row: Order) => <OrdersOverflowMenu order={row} />
  }
};
